import { paintClippedGradient } from './clippedTextGradient.js';
/**
 * THE "UNVEIL" CHOREOGRAPHY — the premium entrance (founder, 2026-09-20: "pas une simple animation mais
 * quelque chose de très grande qualité CSS… on affiche l'image d'abord, après vient le texte un par un,
 * ligne par ligne… dans TOUS les composants, même les compliqués").
 *
 * The section-level reveals in revealInit.ts move a section, or its listed items, as opaque blocks. That
 * is one primitive. This file is the second one: when a section's animation is `reveal`, the engine walks
 * the section's real DOM the moment it arrives and CHOREOGRAPHS what is inside it —
 *
 *   · every picture / video / embed is unmasked first: its frame wipes open from the bottom while the
 *     picture itself settles from 112% to 100% (the Apple / Huawei product-page signature);
 *   · every heading and paragraph is split into its ACTUAL rendered lines — measured, not guessed — and
 *     each line rises into view from behind its own mask, one after the other;
 *   · buttons, icons and form controls follow with a short lift-and-fade;
 *   · a section that lists items (`[data-aqx-anim-item]`: the cards of a grid, the two columns of an
 *     image-with-text, the rows of a table) plays that same score per item, the items offset like a wave.
 *
 * All of that is CSS (`@keyframes aqx-unveil-*` in theme.css). This module only decides WHO moves, in
 * WHICH order, with WHAT delay — and, for text, builds the line boxes the CSS needs, because no
 * stylesheet can know where a sentence wraps.
 *
 * ## Why it is generic instead of per-section
 *
 * Sixty-five sections, every one with a different shape. A per-section script would be sixty-five
 * scripts, drifting. Walking the DOM by ROLE (media, text, action, container) gives every section the
 * choreography for free, including the ones written next month — and the seller's mental model stays
 * one setting: "Unveil".
 *
 * ## The DOM is left EXACTLY as it was
 *
 * Splitting text into lines means replacing an element's children with wrapper spans. That is fine for
 * one second and a liability forever: a resize re-wraps the words, a screen reader meets a hundred spans,
 * and in the builder React still owns those nodes. So the ORIGINAL child nodes are kept aside (the very
 * same objects, not copies — React may update their text meanwhile and that update must survive), and
 * put back the moment the last line has landed. After the choreography nothing remains: no wrapper, no
 * attribute, no inline delay. A section that has played is byte-for-byte the section that was rendered.
 *
 * The one thing that could break that promise is React re-rendering the split element WHILE it plays
 * (`dangerouslySetInnerHTML` replaces every child). The restore checks that our wrappers are still the
 * element's children before putting anything back; if React has already replaced them, React's content
 * is the truth and we touch nothing — never a duplicated paragraph.
 */
/** On an element that plays one beat of the score: `media` | `media-bare` | `line` | `mask` | `soft` | `item`. */
export const UNIT_ATTR = 'data-aqx-anim-unit';
/** On the box that MASKS a framed picture while the picture settles inside it. */
export const FRAME_ATTR = 'data-aqx-anim-frame';
/** On the static, clipped wrapper around one rendered line of text. */
export const LINE_ATTR = 'data-aqx-anim-line';
/** Inline custom property carrying each unit's own delay, in seconds. */
export const DELAY_VAR = '--aqx-anim-d';
/** The `animation` setting value that turns this choreography on. */
export const REVEAL_NAME = 'reveal';
/** Fallback when the section carries no `--aqx-anim-duration` (it always does — see animationVars). */
const DEFAULT_DURATION = 0.9;
/** The cascade never keeps the reader waiting past this, however long the section is. */
const MAX_LAST_DELAY = 1.8;
/** Past this many lines the paragraph is a wall of text; it rises as one masked block instead. */
const MAX_LINES = 10;
/** The short fade an item container gets so a card's background never pops in under its content. */
export const ITEM_FADE = 0.35;
const INLINE_TAGS = new Set([
    'A',
    'SPAN',
    'STRONG',
    'B',
    'EM',
    'I',
    'U',
    'S',
    'SMALL',
    'SUP',
    'SUB',
    'MARK',
    'CODE',
    'ABBR',
    'TIME',
    'BR',
    'WBR',
    'BDI',
    'BDO',
    'Q',
    'CITE',
    'DFN',
    'KBD',
    'VAR',
    'DEL',
    'INS',
]);
const MEDIA_TAGS = new Set(['IMG', 'VIDEO', 'IFRAME', 'CANVAS']);
const SKIP_TAGS = new Set(['STYLE', 'SCRIPT', 'TEMPLATE', 'NOSCRIPT', 'LINK', 'META']);
const SOFT_TAGS = new Set(['SVG', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'PROGRESS', 'METER']);
/** Elements a text can be split inside: block-level flow, where a `display:block` line wrapper changes nothing. */
const SPLITTABLE_DISPLAY = new Set([
    'block',
    'list-item',
    'flow-root',
    'inline-block',
    'table-cell',
    'table-caption',
]);
const inFlight = new WeakMap();
/* ────────────────────────────────────────────────────────────────────────────────────────────────────
 * Classification
 * ──────────────────────────────────────────────────────────────────────────────────────────────────── */
function isInlineOnly(el) {
    for (const child of Array.from(el.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE)
            continue;
        if (child.nodeType !== Node.ELEMENT_NODE)
            continue;
        const e = child;
        if (!INLINE_TAGS.has(e.tagName.toUpperCase()))
            return false;
        if (!isInlineOnly(e))
            return false;
    }
    return true;
}
function hasOwnText(el) {
    for (const child of Array.from(el.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE && (child.textContent ?? '').trim() !== '')
            return true;
    }
    return false;
}
function isHidden(el) {
    return el.hidden || el.getAttribute('aria-hidden') === 'true';
}
/**
 * Walk one section and list its beats in document order, grouped by item.
 *
 * Every `[data-aqx-anim-item]` opens a group (the item container itself is the group's first beat, a
 * short fade). Anything outside an item is a group of its own, so a heading above a grid leads, the
 * cards follow as a wave, and a "view all" button below closes.
 */
function collect(section) {
    const units = [];
    let groups = 0;
    const visit = (el, group) => {
        // Upper-cased on purpose: an inline `<svg>` keeps its SVG-namespace, lower-case tagName.
        const tag = el.tagName.toUpperCase();
        if (SKIP_TAGS.has(tag) || isHidden(el))
            return;
        // A block with its own text engine (Split / Blur / Typing) plays itself; two engines on one heading
        // would each split the other's words. `data-aqx-textanim` is the mark of a block that HAS such an
        // effect — not `data-aqx-ta-bound`, which the React hook puts on every heading it has looked at,
        // effect or none, and which is not a reason to skip a heading that nothing else will animate.
        if (el.hasAttribute('data-aqx-textanim'))
            return;
        // Inside an item, everything shares the item's group. Outside any item, each beat (a picture, a
        // text, a control) is a group of its own — plain containers never open one.
        let own = group;
        if (el.hasAttribute('data-aqx-anim-item')) {
            own = groups++;
            units.push({ el, kind: 'item', group: own });
        }
        if (MEDIA_TAGS.has(tag)) {
            units.push({ el, kind: 'media', group: own < 0 ? groups++ : own });
            return;
        }
        if (SOFT_TAGS.has(tag)) {
            units.push({ el, kind: 'soft', group: own < 0 ? groups++ : own });
            return;
        }
        if (tag === 'PICTURE') {
            for (const child of Array.from(el.children))
                visit(child, own);
            return;
        }
        if (hasOwnText(el)) {
            // A leaf of text (heading, paragraph, cell, label…) is the unit that gets its lines measured; a box
            // that mixes its own text with block children (a price row, a badge beside a title) rises whole.
            const kind = isInlineOnly(el) ? 'line' : 'mask';
            units.push({ el, kind, group: own < 0 ? groups++ : own });
            return;
        }
        if (isInlineOnly(el) && (el.textContent ?? '').trim() !== '') {
            // `<h2><strong>Title</strong></h2>`: no text of its own, but nothing under it is a box either.
            units.push({ el, kind: 'line', group: own < 0 ? groups++ : own });
            return;
        }
        for (const child of Array.from(el.children))
            visit(child, own);
    };
    for (const child of Array.from(section.children))
        visit(child, -1);
    return units;
}
/**
 * The box a picture is unmasked through: its parent, when that parent is a real box holding nothing
 * but this one picture (the usual `<div class="…__media"><img/></div>`). A parent that also holds text, a
 * second picture or a control is not a frame — the picture then wipes open on its own, without the
 * settle, rather than dragging a caption or a sibling under its mask.
 */
function frameOf(media, section) {
    let p = media.parentElement;
    while (p && p !== section && p.tagName.toUpperCase() === 'PICTURE')
        p = p.parentElement;
    if (!p || p === section)
        return undefined;
    if (p.hasAttribute('data-aqx-band'))
        return undefined;
    // A listed ITEM that holds nothing but the picture — the media column of an image-with-text, the
    // tile of an image rail — is the picture's frame, and the wipe replaces its item fade (see below).
    if ((p.textContent ?? '').trim() !== '')
        return undefined;
    const medias = p.querySelectorAll('img, video, iframe, canvas');
    if (medias.length !== 1)
        return undefined;
    const display = getComputedStyle(p).display;
    if (display === 'inline' || display === 'contents' || display === 'none')
        return undefined;
    return p;
}
/**
 * Phase 1: replace the element's children with COPIES whose words are wrapped in plain inline spans.
 * Inline spans change no line break, so measuring them is measuring the original text.
 */
function wrapWords(el) {
    const doc = el.ownerDocument;
    const originals = Array.from(el.childNodes);
    const tokens = [];
    const frag = doc.createDocumentFragment();
    const walk = (node, into, path) => {
        for (const child of Array.from(node.childNodes)) {
            if (child.nodeType === Node.TEXT_NODE) {
                const text = child.textContent ?? '';
                for (const part of text.split(/(\s+)/)) {
                    if (part === '')
                        continue;
                    if (/^\s+$/.test(part)) {
                        const t = doc.createTextNode(' ');
                        into.appendChild(t);
                        tokens.push({ node: t, path, isSpace: true, isBreak: false });
                    }
                    else {
                        const span = doc.createElement('span');
                        span.textContent = part;
                        into.appendChild(span);
                        tokens.push({ node: span, path, isSpace: false, isBreak: false });
                    }
                }
            }
            else if (child.nodeType === Node.ELEMENT_NODE) {
                const e = child;
                if (e.tagName.toUpperCase() === 'BR') {
                    const br = doc.createElement('br');
                    into.appendChild(br);
                    tokens.push({ node: br, path, isSpace: false, isBreak: true });
                    continue;
                }
                const clone = e.cloneNode(false);
                into.appendChild(clone);
                walk(e, clone, [...path, e]);
            }
        }
    };
    walk(el, frag, []);
    el.replaceChildren(frag);
    return { originals, tokens };
}
/** Phase 2 (read): the line index of every word, from where the browser actually put it. */
function lineIndexes(tokens) {
    const out = [];
    let line = -1;
    let lastTop = Number.NaN;
    let lastHeight = 0;
    let pendingBreak = false;
    for (const t of tokens) {
        if (t.isSpace) {
            out.push(line);
            continue;
        }
        if (t.isBreak) {
            pendingBreak = true;
            out.push(line);
            continue;
        }
        const rect = t.node.getClientRects()[0];
        const top = rect ? rect.top : 0;
        const height = rect ? rect.height : 0;
        // A new line when the word sits clearly lower than the previous one (half a line box, so a
        // sub-pixel wobble between two words of the same line never counts), or after a <br>.
        const gap = Number.isNaN(lastTop) ? Number.POSITIVE_INFINITY : Math.abs(top - lastTop);
        if (line < 0 || pendingBreak || gap > Math.max(lastHeight, height) * 0.5)
            line += 1;
        pendingBreak = false;
        lastTop = top;
        lastHeight = height || lastHeight;
        out.push(line);
    }
    return out;
}
/**
 * Phase 3 (write): one `display:block` wrapper per line, its words inside, and every inline ancestor
 * (`<a>`, `<strong>`, a coloured `<span>`) re-created on each line it spans — "deep slicing", so a link
 * that wraps stays a link on both of its lines.
 */
function buildLines(el, tokens, lines, attrs = { lineAttr: LINE_ATTR, unitAttr: UNIT_ATTR }) {
    const doc = el.ownerDocument;
    const wrappers = [];
    const inners = [];
    let current = -1;
    let inner = null;
    let chain = [];
    const open = (i) => {
        const wrap = doc.createElement('span');
        wrap.setAttribute(attrs.lineAttr, '');
        const inn = doc.createElement('span');
        inn.setAttribute(attrs.unitAttr, 'line');
        wrap.appendChild(inn);
        wrappers.push(wrap);
        inners.push(inn);
        inner = inn;
        chain = [];
        current = i;
    };
    const target = (path) => {
        let depth = 0;
        while (depth < chain.length && depth < path.length && chain[depth].src === path[depth])
            depth++;
        chain.length = depth;
        let parent = depth === 0 ? inner : chain[depth - 1].clone;
        for (let d = depth; d < path.length; d++) {
            const clone = path[d].cloneNode(false);
            parent.appendChild(clone);
            chain.push({ src: path[d], clone });
            parent = clone;
        }
        return parent;
    };
    // The line of the next WORD after each token, so a space knows whether it sits between two words of
    // the same line (kept) or at a line's edge (dropped: a block wrapper already breaks there).
    const nextWordLine = new Array(tokens.length).fill(-1);
    for (let k = tokens.length - 1, seen = -1; k >= 0; k--) {
        nextWordLine[k] = seen;
        if (!tokens[k].isSpace && !tokens[k].isBreak)
            seen = lines[k];
    }
    tokens.forEach((t, k) => {
        if (t.isBreak)
            return;
        const line = lines[k];
        if (t.isSpace) {
            if (line !== current || nextWordLine[k] !== line)
                return;
            target(t.path).appendChild(doc.createTextNode(' '));
            return;
        }
        if (line !== current)
            open(line);
        // The measuring span has done its job: the line gets the bare word back.
        target(t.path).appendChild(doc.createTextNode(t.node.textContent ?? ''));
    });
    el.replaceChildren(...wrappers);
    return { wrappers, inners };
}
/**
 * Take ONE text element apart into the lines the browser actually rendered — the same three phases the
 * unveil runs on a whole section, offered to the BLOCK text animation ("Split by → Lines", founder
 * 21/09/2026: a heading written one word per line must reveal one line after another, "venant du
 * top"). Returns `null` when the element is not a box whose lines can be measured (flex/grid/`pre`),
 * holds no line, or holds more than `maxLines`; then nothing has been changed.
 *
 * The attribute names are the caller's: `theme.css` drives `[data-aqx-anim-unit='line']` under any
 * `[data-aqx-anim-in]` band, so a block inside an animated section must wear different ones or it
 * would be animated twice.
 */
export function sliceLines(el, opts) {
    const { originals, tokens } = wrapWords(el);
    const cs = getComputedStyle(el);
    const splittable = SPLITTABLE_DISPLAY.has(cs.display) && !cs.whiteSpace.startsWith('pre');
    const lines = splittable ? lineIndexes(tokens) : null;
    const count = lines ? Math.max(...lines) + 1 : 0;
    if (!lines || count < 1 || count > (opts.maxLines ?? MAX_LINES)) {
        el.replaceChildren(...originals);
        return null;
    }
    const { wrappers, inners } = buildLines(el, tokens, lines, opts);
    return { wrappers, inners, restore: () => restore({ el, originals, wrappers }) };
}
/**
 * The text leaves under `root` — the elements whose children are inline only, so their lines can be
 * measured. `root` itself when it is one (a heading); its `<p>`/`<li>` when it is a rich-text body.
 */
export function textLeaves(root) {
    if (isInlineOnly(root))
        return (root.textContent ?? '').trim() !== '' ? [root] : [];
    return Array.from(root.querySelectorAll('*')).filter((e) => !INLINE_TAGS.has(e.tagName.toUpperCase()) &&
        !SKIP_TAGS.has(e.tagName.toUpperCase()) &&
        isInlineOnly(e) &&
        (e.textContent ?? '').trim() !== '');
}
/** Put the original nodes back — only if nothing else has touched the element meanwhile. */
function restore(split) {
    const { el, originals, wrappers } = split;
    const kids = Array.from(el.childNodes);
    const intact = kids.length === wrappers.length && wrappers.every((w, i) => kids[i] === w);
    if (!intact)
        return;
    el.replaceChildren(...originals);
}
/* ────────────────────────────────────────────────────────────────────────────────────────────────────
 * The score
 * ──────────────────────────────────────────────────────────────────────────────────────────────────── */
function durationOf(section) {
    const raw = section.style.getPropertyValue('--aqx-anim-duration').trim();
    if (!raw)
        return DEFAULT_DURATION;
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n <= 0)
        return DEFAULT_DURATION;
    return raw.endsWith('ms') ? n / 1000 : n;
}
const fmt = (s) => `${Math.round(s * 1000) / 1000}s`;
/**
 * Delays, in seconds, for every beat. Pace scales everything (`k`): a "slow" unveil is slower in its
 * gaps too, or the gaps would vanish under the longer movement.
 *
 * Within a group the picture leads, then the text lines and controls follow in reading order. Groups
 * (items) start `groupStep` apart — and a group whose first beat is a picture holds the next group back
 * a little more, so the picture is visibly FIRST rather than merely earliest.
 */
function schedule(units, D) {
    const k = D / DEFAULT_DURATION;
    const delays = new Map();
    const groupIds = Array.from(new Set(units.map((u) => u.group)));
    const G = groupIds.length;
    let groupStep = 0.12 * k;
    if (G > 1)
        groupStep = Math.min(groupStep, 1.0 / (G - 1));
    let start = 0;
    for (const g of groupIds) {
        const members = units.filter((u) => u.group === g);
        const item = members.find((u) => u.kind === 'item');
        const medias = members.filter((u) => u.kind === 'media' || u.kind === 'media-bare');
        const rest = members.filter((u) => u.kind !== 'item' && u.kind !== 'media' && u.kind !== 'media-bare');
        if (item)
            delays.set(item.el, start);
        const inner = item ? start + 0.06 * k : start;
        // Beats: each picture, then each LINE of each text, then each control — the count decides the step,
        // so a paragraph of eight lines and a title of one both finish inside the same breath.
        const beats = [];
        for (const m of medias)
            beats.push(m.el);
        const lead = medias.length ? 0.16 * k : 0;
        const textBeats = [];
        for (const u of rest) {
            if (u.kind === 'line' && u.lines?.length)
                textBeats.push(...u.lines);
            else
                textBeats.push(u.el);
        }
        const B = medias.length + textBeats.length;
        let step = 0.09 * k;
        if (B > 1)
            step = Math.min(step, 0.6 / (B - 1));
        medias.forEach((m, i) => {
            delays.set(m.el, inner + i * step);
            if (m.frame)
                delays.set(m.frame, inner + i * step);
        });
        textBeats.forEach((el, j) => delays.set(el, inner + lead + (medias.length + j) * step));
        start += groupStep + (medias.length ? 0.15 * k : 0);
    }
    // However long the section, the last beat starts inside the reader's patience.
    let max = 0;
    for (const d of delays.values())
        if (d > max)
            max = d;
    if (max > MAX_LAST_DELAY) {
        const f = MAX_LAST_DELAY / max;
        for (const [el, d] of delays)
            delays.set(el, d * f);
    }
    return delays;
}
/**
 * Take the choreography off a section: original text nodes back, every marker and delay removed.
 * Idempotent, and safe to call on a section that never played.
 */
export function settleChoreo(section) {
    const flight = inFlight.get(section);
    if (!flight)
        return;
    inFlight.delete(section);
    clearTimeout(flight.timer);
    for (const s of flight.splits)
        restore(s);
    for (const { el, style } of flight.marked) {
        el.removeAttribute(UNIT_ATTR);
        el.removeAttribute(FRAME_ATTR);
        el.style.removeProperty(DELAY_VAR);
        unmarkStyle(el, style);
    }
}
/**
 * Hand the `style` attribute back as it was — unless something else (React, another engine) wrote to
 * it meanwhile, in which case THEIR value stands. Touching a style declaration re-serialises the whole
 * attribute (`display:block` → `display: block;`), so "as it was" needs the original text, and "nobody
 * else wrote" is checked by normalising that text the same way on a scratch element.
 */
function unmarkStyle(el, original) {
    if (original === null) {
        if (el.style.length === 0)
            el.removeAttribute('style');
        return;
    }
    const scratch = el.ownerDocument.createElement('span');
    scratch.setAttribute('style', original);
    if (scratch.style.cssText === el.style.cssText)
        el.setAttribute('style', original);
}
/**
 * Choreograph the sections that have just arrived, TOGETHER: every write of phase 1 for all of them,
 * then every read, then every write of phase 3 — one forced layout for the whole batch instead of one
 * per section (a home page can bring five sections over the line on one scroll tick).
 *
 * Marks are all in place when this returns, so the caller can set the arrival flag in the same task and
 * the browser resolves everything in one style pass — no frame with the content shown finished.
 */
export function choreograph(sections) {
    const jobs = sections.map((section) => {
        settleChoreo(section);
        return { section, units: collect(section), D: durationOf(section) };
    });
    const pending = [];
    for (const job of jobs) {
        for (const unit of job.units) {
            if (unit.kind !== 'line')
                continue;
            pending.push({ job, unit, ...wrapWords(unit.el) });
        }
    }
    // Phase 2 — reads: where did the browser put each word; is the element a box we may split; which
    // pictures have a frame of their own.
    const measured = [];
    for (const p of pending) {
        const cs = getComputedStyle(p.unit.el);
        const splittable = SPLITTABLE_DISPLAY.has(cs.display) && !cs.whiteSpace.startsWith('pre');
        const lines = splittable ? lineIndexes(p.tokens) : null;
        const count = lines ? Math.max(...lines) + 1 : 0;
        measured.push({ ...p, lines: lines && count >= 1 && count <= MAX_LINES ? lines : null });
    }
    for (const job of jobs) {
        for (const unit of job.units) {
            if (unit.kind !== 'media')
                continue;
            unit.frame = frameOf(unit.el, job.section);
            if (!unit.frame)
                unit.kind = 'media-bare';
        }
        // An item that IS a picture's frame plays the wipe, not the item fade: one element, one animation.
        const frames = new Set(job.units.map((u) => u.frame).filter(Boolean));
        job.units = job.units.filter((u) => !(u.kind === 'item' && frames.has(u.el)));
    }
    // Phase 3 — writes: line boxes for the splittable texts, originals back for the rest, marks and
    // delays for everyone.
    const splitsBySection = new Map();
    for (const job of jobs)
        splitsBySection.set(job.section, []);
    for (const m of measured) {
        if (m.lines) {
            const { wrappers, inners } = buildLines(m.unit.el, m.tokens, m.lines);
            m.unit.lines = inners;
            splitsBySection.get(m.job.section).push({ el: m.unit.el, originals: m.originals, wrappers });
        }
        else {
            m.unit.el.replaceChildren(...m.originals);
            m.unit.kind = 'mask';
        }
    }
    // The lines are blocks already (theme.css) and not yet moving (the arrival flag comes after this):
    // a gradient heading's ink goes onto each of its lines here, or a composited line would paint
    // nothing at all — see clippedTextGradient.ts.
    paintClippedGradient(measured.flatMap((m) => (m.lines ? (m.unit.lines ?? []) : [])));
    for (const job of jobs) {
        const delays = schedule(job.units, job.D);
        const marked = [];
        let last = 0;
        for (const unit of job.units) {
            const d = delays.get(unit.el) ?? 0;
            if (unit.kind === 'line' && unit.lines?.length) {
                for (const line of unit.lines) {
                    const ld = delays.get(line) ?? d;
                    line.style.setProperty(DELAY_VAR, fmt(ld));
                    if (ld > last)
                        last = ld;
                }
                continue;
            }
            marked.push({ el: unit.el, style: unit.el.getAttribute('style') });
            unit.el.setAttribute(UNIT_ATTR, unit.kind);
            unit.el.style.setProperty(DELAY_VAR, fmt(d));
            if (unit.frame) {
                marked.push({ el: unit.frame, style: unit.frame.getAttribute('style') });
                unit.frame.setAttribute(FRAME_ATTR, '');
                unit.frame.style.setProperty(DELAY_VAR, fmt(d));
            }
            if (d > last)
                last = d;
        }
        const splits = splitsBySection.get(job.section) ?? [];
        const total = last + job.D + 0.1;
        const timer = window.setTimeout(() => settleChoreo(job.section), total * 1000);
        inFlight.set(job.section, { timer, splits, marked });
    }
}
/** Whether a section's inline animation is the unveil — the only one this module choreographs. */
export function isUnveil(section) {
    return (section.style.getPropertyValue('--aqx-anim-name').trim() === REVEAL_NAME ||
        section.getAttribute('data-aqx-anim') === REVEAL_NAME);
}
//# sourceMappingURL=revealChoreo.js.map